import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  SignalIcon,
  EyeIcon,
  EyeSlashIcon,
  NoSymbolIcon,
  TrashIcon,
  ShieldCheckIcon,
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { fetchManagedTenants, activateTenant, probeTenantLicense } from '../api/client';
import OnboardingDrawer from './OnboardingDrawer';
import EditTenantDrawer from './EditTenantDrawer';
import BackfillTenantDrawer from './BackfillTenantDrawer';
import Pagination from './Pagination';
import Badge from './ui/Badge';
import { tenantStateTone, cloudEnvironmentTone } from './ui/badgeTones';
import { RefreshButton, LoadingCard } from './ui/LoadingState';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from '../utils/demoMode';
import { isInactiveStatus, lifecycleBadge } from '../utils/tenantLifecycle';

// In Demo Mode the consent URL renders with the MASKED tenant id so the flow can
// still be talked through — but the masked URL is deliberately non-functional, so
// copy/open is disabled rather than handing out a broken link.
const DEMO = isDemoMode();

// Best-known "monitored since" date for a tenant. Prefers the authoritative portal
// timestamps (onboarded → activated); falls back to the inferred earliest-log-event
// date (firstEventUtc), which is only approximate. Returns { iso, approx }.
const monitoredSince = (t) => {
  if (t.onboardedUtc) return { iso: t.onboardedUtc, approx: false };
  if (t.activatedUtc) return { iso: t.activatedUtc, approx: false };
  if (t.firstEventUtc) return { iso: t.firstEventUtc, approx: true };
  return { iso: '', approx: false };
};

// Date-only display (UTC) for an ISO 8601 string; '—' when absent/invalid.
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [activateError, setActivateError] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [consentTenant, setConsentTenant] = useState(null);
  const [consentCopied, setConsentCopied] = useState(false);
  const [probingId, setProbingId] = useState(null);
  const [probeResults, setProbeResults] = useState(null);
  const [probeError, setProbeError] = useState(null);
  const [backfillTenant, setBackfillTenant] = useState(null);
  const [sortColumn, setSortColumn] = useState('friendlyName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showInactive, setShowInactive] = useState(false);
  const [mspFilter, setMspFilter] = useState('all');
  const [pageSize, setPageSize] = useState(10);

  // Distinct MSP codes present in the loaded tenants, for the MSP filter dropdown.
  const mspOptions = [...new Set(tenants.map((t) => t.msp).filter(Boolean))].sort();

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchManagedTenants();
      setTenants(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchManagedTenants();
        if (!cancelled) setTenants(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter tenants: hide inactive/deleted unless toggled, then apply search
  const filtered = tenants.filter((t) => {
    if (!showInactive && (t.status === 'inactive' || t.status === 'deleted')) return false;
    if (mspFilter !== 'all') {
      if (mspFilter === '__none__') {
        if (t.msp) return false;
      } else if ((t.msp || '') !== mspFilter) {
        return false;
      }
    }
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.friendlyName || '').toLowerCase().includes(q) ||
      (t.domain || '').toLowerCase().includes(q) ||
      (t.tenantId || '').toLowerCase().includes(q) ||
      (t.shortName || '').toLowerCase().includes(q) ||
      (t.msp || '').toLowerCase().includes(q)
    );
  });

  // Sort ('monitoredSince' is synthetic — derived from the 3 date fields; ISO 8601
  // strings sort lexically = chronologically, so localeCompare works for it too.)
  const sorted = sortColumn
    ? [...filtered].sort((a, b) => {
        const pick = (t) => (sortColumn === 'monitoredSince' ? monitoredSince(t).iso : t[sortColumn]);
        const aVal = (pick(a) || '').toLowerCase();
        const bVal = (pick(b) || '').toLowerCase();
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === 'desc' ? -cmp : cmp;
      })
    : filtered;

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  // Reset to page 1 when search or MSP filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, mspFilter]);

  const handleActivate = async (tenantId) => {
    setActivatingId(tenantId);
    setActivateError(null);
    try {
      await activateTenant(tenantId);
      // Refresh the list to show updated status
      await loadTenants();
    } catch (err) {
      setActivateError(`Failed to activate ${tenantId}: ${err.message}`);
    } finally {
      setActivatingId(null);
    }
  };

  const handleProbeTenant = async (tenantId) => {
    setProbingId(tenantId);
    setProbeError(null);
    try {
      const data = await probeTenantLicense(tenantId);
      setProbeResults(data);
      await loadTenants();
    } catch (err) {
      setProbeError(`Probe failed for ${tenantId}: ${err.message}`);
    } finally {
      setProbingId(null);
    }
  };

  const handleOnboardSuccess = () => {
    loadTenants();
  };

  const tierLabel = (tier) => {
    switch (tier) {
      case 'Defender': return 'Defender';
      case 'Identity': return 'Identity';
      case 'Basic': return 'Basic';
      default: return tier || '—';
    }
  };

  // Government clouds get a badge; Commercial (and blank, which means Commercial) get nothing —
  // almost every tenant is commercial, so a pill on every row would be pure noise. Any
  // unrecognised value is treated as Commercial, matching the log-collection contract.
  const cloudEnvLabel = (env) => {
    switch (String(env || '').toLowerCase()) {
      case 'gcc': return 'GCC';
      case 'gcchigh': return 'GCC High';
      case 'dod': return 'DoD';
      default: return null;
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Managed Tenants</h1>
          <p className="text-sm text-gray-400 mt-1">
            Onboard and manage M365 client tenants for log collection.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowInactive((prev) => !prev)}
            className={`btn-secondary flex items-center gap-2 ${showInactive ? 'border-amber-600/50 text-amber-400' : ''}`}
            title={showInactive ? 'Hide paused, retained and pending-deletion tenants' : 'Show paused, retained and pending-deletion tenants'}
          >
            {showInactive ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </button>
          <RefreshButton
            loading={loading}
            onClick={loadTenants}
            className="btn-secondary flex items-center gap-2"
            iconClassName="h-4 w-4"
          />
          <button
            onClick={() => setDrawerOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Onboard Tenant
          </button>
        </div>
      </div>

      {/* Search + MSP filter */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, domain, tenant ID, or MSP..."
            className="input-field pl-10"
          />
        </div>
        <select
          value={mspFilter}
          onChange={(e) => setMspFilter(e.target.value)}
          className="input-field w-auto min-w-[10rem]"
          title="Filter by MSP"
        >
          <option value="all">All MSPs</option>
          {mspOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
          <option value="__none__">(No MSP)</option>
        </select>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-300">Failed to load tenants</p>
            <p className="text-sm text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Activate Error Banner */}
      {activateError && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-300">Activation failed</p>
            <p className="text-sm text-red-400 mt-1">{activateError}</p>
          </div>
          <button onClick={() => setActivateError(null)} className="text-red-400 hover:text-red-300 ml-auto">
            &times;
          </button>
        </div>
      )}

      {/* Probe Error Banner */}
      {probeError && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-300">License probe failed</p>
            <p className="text-sm text-red-400 mt-1">{probeError}</p>
          </div>
          <button onClick={() => setProbeError(null)} className="text-red-400 hover:text-red-300 ml-auto">
            &times;
          </button>
        </div>
      )}

      {/* Probe Results Banner */}
      {probeResults && (
        <div className={`mb-4 p-4 rounded-lg border ${
          probeResults.totalErrors > 0
            ? 'bg-amber-900/30 border-amber-700/50'
            : 'bg-green-900/30 border-green-700/50'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <SignalIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                probeResults.totalErrors > 0 ? 'text-amber-400' : 'text-green-400'
              }`} />
              <div>
                <p className="text-sm font-medium text-gray-200">License Probe Complete</p>
                <p className="text-sm text-gray-400 mt-1">
                  {probeResults.totalProbed} probed, {probeResults.totalChanged} changed, {probeResults.totalErrors} errors
                  {probeResults.csvUpdated && ' — CSV updated'}
                </p>
                {(probeResults.totalChanged > 0 || probeResults.totalErrors > 0) && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 transition-colors">
                      Show details
                    </summary>
                    <div className="mt-2 space-y-1">
                      {probeResults.results
                        .filter((r) => r.changed || r.error)
                        .map((r) => (
                          <div key={r.tenantId} className="text-xs text-gray-400">
                            <span className="font-medium text-gray-300">{r.friendlyName}</span>
                            {r.changed && (
                              <span>: {tierLabel(r.previousTier)} → {tierLabel(r.detectedTier)}</span>
                            )}
                            {r.error && (
                              <span className="text-red-400"> Error: {r.error}</span>
                            )}
                          </div>
                        ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
            <button
              onClick={() => setProbeResults(null)}
              className="text-gray-400 hover:text-gray-300"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <LoadingCard message="Loading tenants…" />
      )}

      {/* Tenant Table */}
      {!loading && !error && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors"
                    onClick={() => handleSort('shortName')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Short Name
                      {sortColumn === 'shortName' && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400" />
                      )}
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors"
                    onClick={() => handleSort('friendlyName')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Tenant
                      {sortColumn === 'friendlyName' && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400" />
                      )}
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors"
                    onClick={() => handleSort('domain')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Domain
                      {sortColumn === 'domain' && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400" />
                      )}
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors"
                    onClick={() => handleSort('msp')}
                  >
                    <span className="inline-flex items-center gap-1">
                      MSP
                      {sortColumn === 'msp' && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400" />
                      )}
                    </span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">License Tier</th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    title="Microsoft cloud the tenant lives in. Blank means Commercial."
                  >
                    Cloud
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Auth</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors"
                    onClick={() => handleSort('monitoredSince')}
                    title="When the tenant was added to be monitored"
                  >
                    <span className="inline-flex items-center gap-1">
                      Monitored Since
                      {sortColumn === 'monitoredSince' && (
                        sortDirection === 'asc'
                          ? <ChevronUpIcon className="h-3 w-3 text-blue-400" />
                          : <ChevronDownIcon className="h-3 w-3 text-blue-400" />
                      )}
                    </span>
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {paged.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center text-gray-500">
                      {searchQuery || mspFilter !== 'all' ? 'No tenants match your filters.' : 'No tenants configured yet.'}
                    </td>
                  </tr>
                )}
                {paged.map((tenant) => (
                  <tr key={tenant.tenantId} className={`hover:bg-gray-800/30 transition-colors ${tenant.status === 'inactive' ? 'opacity-50' : ''} ${tenant.status === 'deleted' ? 'opacity-40' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-300">{tenant.shortName || '—'}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-200">{tenant.friendlyName || '—'}</p>
                        <p className="text-xs text-gray-500 font-mono">{tenant.tenantId}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{tenant.domain || '—'}</td>
                    <td className="px-4 py-3">
                      {tenant.msp
                        ? <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-sky-900/40 text-sky-300 border-sky-700/50">{tenant.msp}</span>
                        : <span className="text-xs text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{tierLabel(tenant.licenseTier)}</td>
                    <td className="px-4 py-3">
                      {cloudEnvLabel(tenant.cloudEnvironment)
                        ? (
                          <Badge tone={cloudEnvironmentTone(tenant.cloudEnvironment)}>
                            {cloudEnvLabel(tenant.cloudEnvironment)}
                          </Badge>
                        )
                        : <span className="text-xs text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-400">
                        {tenant.authMethod === 'FederatedIdentity' ? 'FIC' : tenant.authMethod || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tenant.status === 'active' ? (
                        <Badge tone={tenantStateTone('active')}>
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Active
                        </Badge>
                      ) : isInactiveStatus(tenant.status) ? (
                        // Inactive lifecycle (2026-09-29): Paused / Retained / "Deletes in N days" / Awaiting log
                        // deletion. Legacy status=deleted rows render as a pending deletion (deletedDate + 30).
                        (() => {
                          const b = lifecycleBadge(tenant);
                          const pending = b && b.tone === 'critical';
                          return (
                            <Badge tone={b ? b.tone : tenantStateTone('inactive')}>
                              {pending ? <TrashIcon className="h-3.5 w-3.5" /> : <NoSymbolIcon className="h-3.5 w-3.5" />}
                              {b ? b.label : 'Inactive'}
                            </Badge>
                          );
                        })()
                      ) : (
                        <Badge tone={tenantStateTone('pending')}>
                          <ClockIcon className="h-3.5 w-3.5" />
                          Pending
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                      {(() => {
                        const ms = monitoredSince(tenant);
                        if (!ms.iso) return <span className="text-gray-600">—</span>;
                        return (
                          <span
                            className="inline-flex items-center gap-1"
                            title={ms.approx
                              ? 'Approximate — earliest log data seen (UTC); no portal onboarding record'
                              : `${tenant.onboardedUtc ? 'Onboarded' : 'Activated'} ${ms.iso} (UTC)`}
                          >
                            {ms.approx && <span className="text-gray-500">≈</span>}
                            {fmtDate(ms.iso)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => handleProbeTenant(tenant.tenantId)}
                          disabled={probingId === tenant.tenantId}
                          className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                          title="Probe license tier"
                        >
                          {probingId === tenant.tenantId ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-300" />
                          ) : (
                            <SignalIcon className="h-3.5 w-3.5" />
                          )}
                          Probe
                        </button>
                        {tenant.status === 'active' && (
                          <button
                            onClick={() => setBackfillTenant(tenant)}
                            className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                            title="Backfill historical log data"
                          >
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            Backfill
                          </button>
                        )}
                        <button
                          onClick={() => { setConsentTenant(tenant); setConsentCopied(false); }}
                          className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                          title="Show admin consent URL for this tenant"
                        >
                          <ShieldCheckIcon className="h-3.5 w-3.5" />
                          Consent Link
                        </button>
                        <button
                          onClick={() => setEditingTenant(tenant)}
                          className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                          title="Edit tenant details"
                        >
                          <PencilSquareIcon className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        {tenant.status === 'pending' && (
                          <button
                            onClick={() => handleActivate(tenant.tenantId)}
                            disabled={activatingId === tenant.tenantId}
                            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
                          >
                            {activatingId === tenant.tenantId ? (
                              <>
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                                Activating...
                              </>
                            ) : (
                              <>
                                <PlayIcon className="h-3.5 w-3.5" />
                                Activate
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={sorted.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        </div>
      )}

      {/* Summary counts */}
      {!loading && !error && tenants.length > 0 && (
        <div className="mt-4 flex gap-4 text-xs text-gray-500">
          <span>{tenants.filter((t) => t.status === 'active').length} active</span>
          <span>{tenants.filter((t) => t.status === 'pending').length} pending</span>
          {[
            ['paused', 'paused', ''],
            ['retained', 'retained', ''],
            ['pending_deletion', 'pending deletion', 'text-red-500'],
          ].map(([reason, label, cls]) => {
            const n = tenants.filter((t) => isInactiveStatus(t.status) && t.inactiveReason === reason).length;
            return n > 0 ? <span key={reason} className={cls}>{n} {label}</span> : null;
          })}
          <span>{tenants.length} total</span>
        </div>
      )}

      {/* Onboarding Drawer */}
      <OnboardingDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleOnboardSuccess}
      />

      {/* Edit Tenant Drawer */}
      <EditTenantDrawer
        isOpen={!!editingTenant}
        tenant={editingTenant}
        allTenants={tenants}
        onClose={() => setEditingTenant(null)}
        onSuccess={() => loadTenants()}
      />

      {/* Backfill Tenant Drawer */}
      <BackfillTenantDrawer
        tenant={backfillTenant}
        onClose={() => setBackfillTenant(null)}
      />

      {/* Admin Consent URL modal */}
      {consentTenant && (
        <ConsentUrlModal
          tenant={consentTenant}
          copied={consentCopied}
          onCopy={(url) => {
            navigator.clipboard.writeText(url).then(() => {
              setConsentCopied(true);
              setTimeout(() => setConsentCopied(false), 2000);
            });
          }}
          onClose={() => setConsentTenant(null)}
        />
      )}
    </div>
  );
}

function ConsentUrlModal({ tenant, copied, onCopy, onClose }) {
  const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const url = `https://login.microsoftonline.com/${tenant.tenantId}/adminconsent?client_id=0c9e2aab-0d6f-4bf4-a90a-d3c87727fefa&redirect_uri=${encodeURIComponent(`${appUrl}/consent-complete.html`)}`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-100 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
              Admin Consent URL
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              For <span className="text-gray-300 font-medium">{tenant.friendlyName || tenant.domain || tenant.tenantId}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-3">
          Send this URL to the tenant's Global Admin to (re-)approve Blue365Signal app permissions. They'll see the current permission set in the consent prompt — including any newly added permissions.
        </p>

        <div className="bg-gray-950 border border-gray-800 rounded p-3 mb-4 break-all">
          <code className="text-xs text-gray-300 font-mono">{url}</code>
        </div>

        {DEMO && (
          <p className="text-xs text-amber-400/90 mb-3">
            Demo Mode: the tenant id above is masked, so this URL is not usable — copying is disabled.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
          <button
            onClick={() => onCopy(url)}
            disabled={DEMO}
            title={DEMO ? DEMO_UNAVAILABLE_TITLE : undefined}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copied
              ? <><ClipboardDocumentCheckIcon className="h-4 w-4" /> Copied</>
              : <><ClipboardIcon className="h-4 w-4" /> Copy URL</>}
          </button>
        </div>
      </div>
    </div>
  );
}
